import React, { useCallback, useContext, useEffect, useState, useMemo, useRef } from 'react';
import { useStyles } from '../../../LawPortal.styles';
import {
  ButtonLink,
  HtmlView,
  MTable,
  Transition
} from '../../../../../Components';
import { useIntl } from 'react-intl';
import { CreatePlannig } from './CreatePlanning';
import {
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  ArrowBack,
  ControlPointDuplicate,
  Delete,
  Edit,
  SyncDisabledSharp,
  SyncSharp,
} from '@material-ui/icons';
import { PlanningView } from './View/PlanningView';
import { useAptorApi } from '../../../../../Api';
import {
  UserContext
} from '../../../../../Context/UserContext/UserContext';
import moment from 'moment';
import { useSnackbar } from "notistack";
import { ExecuteFilter } from './ExecuteFilter';
import { useFilter } from '../../../../../Components/ListFilter';
import { MTableToolbar } from '@material-table/core';
import { entityToSelectOption } from '../../../../../Utilities';
import { useNavigate, useLocation } from 'react-router-dom';
import { ModuleContext } from '../../../../../Context/ModuleContext/ModuleContext';
import { Alert } from '@mui/material';

interface IconProps {
  id: number;
  name: string;
  icon?: React.ReactNode;
  onClick?: (event: any, planData?: any) => void;
}

interface PlanData {
  id: string;
  name: string;
  isActive: boolean;
  isDirect: boolean;
  isArchived: boolean;
  startDate: string;
  endDate: string;
  customerCompanyUnitId?: { name: string };
  recurrentInterval?: string;
  lawCount?: number;
  number?: string;
  legislation?: string;
  responsibles?: string;
}

export const OldPlanningLK = React.memo(() => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [newCreate, setNewCreate] = useState(false);
  const { customerId } = useContext(UserContext);
  const { api, abortController } = useAptorApi();
  const [activePlanList, setActivePlan] = useState<PlanData[]>([]);
  const [inActivePlanList, setInActivePlan] = useState<PlanData[]>([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [duplicatePlanData, setDuplicatePlanData] = useState<any>('');
  const [editData, setEditData] = useState<any>("");
  const [filters, setFilter] = useFilter();
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState<'active' | 'all' | 'deactive'>('active');
  const [allControl, setAllControl] = useState<PlanData[]>([]);
  const { setHeader } = useContext(ModuleContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Set header on mount
  useEffect(() => {
    setHeader(
      formatMessage({ id: 'law-portal.legal.compliance.header', defaultMessage: 'Legal compliance control' }),
    );
  }, [setHeader, formatMessage]);

  // Parse URL params on mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryParamsMap = {
      tags: 'tags',
      companyUnit: 'companyUnit',
      complianceUsers: 'complianceUsers',
      processes: 'processes',
      aspects: 'aspects',
      category: 'category',
      area: 'area',
    };

    const searchQuery = queryParams.get('search');
    if (searchQuery) {
      setSearch(searchQuery);
    }

    Object.entries(queryParamsMap).forEach(([queryKey, stateKey]) => {
      const queryValue = queryParams.get(queryKey);
      if (queryValue) {
        setFilter(stateKey, queryValue.split(','));
      }
    });
  }, []);

  // Update URL when filters/search change
  useEffect(() => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value && Array.isArray(value) && value.length > 0) {
        params.set(key, value.join(','));
      }
    });

    if (search) {
      params.set('search', search);
    }

    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    }, { replace: true });
  }, [filters, search, navigate, location.pathname]);

  // Fetch compliance plans
  const getCompliancePlan = useCallback(async () => {
    setPlanLoading(true);
    try {
      const response = await api.getAllcompliancePlan(customerId, {
        filters: filters,
        search: search
      });

      if (!abortController.current.signal.aborted) {
        const compliancePlan = response?.customerPlans?.filter(
          (val: PlanData) => !val.isDirect
        ) || [];

        const sortPlans = (plans: PlanData[]) => {
          return plans.sort((a, b) => {
            const startDateComparison = new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
            return startDateComparison === 0 ? a.name.localeCompare(b.name) : startDateComparison;
          });
        };

        const activePlans = sortPlans(
          compliancePlan.filter((val: PlanData) => val.isActive && val.isArchived !== true)
        );
        const inactivePlans = sortPlans(
          compliancePlan.filter((val: PlanData) => !val.isActive && val.isArchived !== true)
        );

        setActivePlan(activePlans);
        setInActivePlan(inactivePlans);
      }
    } catch (error) {
      console.error('Error fetching compliance plans:', error);
    } finally {
      setPlanLoading(false);
    }
  }, [api, customerId, filters, search, abortController]);

  // Fetch all controls
  const handleAllControl = useCallback(async () => {
    try {
      const response: any = await api.getAllcompliancePlanAllControl(customerId, {
        search: search,
        filters: filters
      });
      
      // Handle IGridQueryResult or direct array response
      const allCompliance = Array.isArray(response) ? response : (response?.data || []);
      
      setAllControl(
        allCompliance.filter(
          (data: PlanData) => !data.isDirect && data.isActive && data.isArchived !== true
        )
      );
    } catch (error) {
      console.error('Error fetching all controls:', error);
      setAllControl([]);
    }
  }, [api, customerId, search, filters]);

  // Fetch data when filters/search change
  useEffect(() => {
    getCompliancePlan();
    handleAllControl();
  }, [getCompliancePlan, handleAllControl]);

  // Action handlers
  const editPlane = useCallback((data: any) => {
    setEditData(data?.planData);
    setNewCreate(true);
  }, []);

  const handleDuplicatePlan = useCallback((data: any) => {
    setDuplicatePlanData(data?.planData);
    setNewCreate(true);
  }, []);

  const handleActiveInActive = useCallback(async (data: any) => {
    try {
      await api.planStatus(data?.planData?.id);
      await Promise.all([getCompliancePlan(), handleAllControl()]);
    } catch (error) {
      console.error('Error toggling plan status:', error);
    }
  }, [api, getCompliancePlan, handleAllControl]);

  const handleDeletePlan = useCallback(async (data: any) => {
    try {
      const res = await api.deletePlan(data?.planData?.id);
      if (res.errorMessage) {
        enqueueSnackbar(formatMessage({ id: res.errorMessage }), { variant: 'error' });
      } else {
        await Promise.all([getCompliancePlan(), handleAllControl()]);
      }
    } catch (error) {
      console.error('Error deleting plan:', error);
    }
  }, [api, enqueueSnackbar, formatMessage, getCompliancePlan, handleAllControl]);

  // Memoize action definitions
  const actionData: IconProps[] = useMemo(() => [
    {
      id: 1,
      name: 'law-portal.legal.plan.copy.duplicate',
      icon: <ControlPointDuplicate />,
      onClick: (event: any, planData: any) => {
        event.stopPropagation();
        handleDuplicatePlan(planData);
      },
    },
    {
      id: 3,
      name: 'law-portal.legal.plan.edit',
      icon: <Edit />,
      onClick: (event: any, planData: any) => {
        event.stopPropagation();
        editPlane(planData);
      },
    },
    {
      id: 4,
      name: 'law-portal.legal.plan.delete',
      icon: <Delete />,
      onClick: (event: any, planData: any) => {
        event.stopPropagation();
        handleDeletePlan(planData);
      },
    },
    {
      id: 5,
      name: 'law-portal.legal.plan.deactivate',
      icon: <SyncDisabledSharp />,
      onClick: (event: any, planData: any) => {
        event.stopPropagation();
        handleActiveInActive(planData);
      },
    },
    {
      id: 6,
      name: 'law-portal.legal.plan.publish',
      icon: <SyncSharp />,
      onClick: (event: any, planData: any) => {
        event.stopPropagation();
        handleActiveInActive(planData);
      },
    },
  ], [handleDuplicatePlan, editPlane, handleDeletePlan, handleActiveInActive]);

  // Memoize icons component
  const IconsNode = useMemo(() => React.memo(({ planData }: any) => {
    const actionIcon = actionData.filter((val: any) => {
      return planData?.isActive
        ? val.name !== 'law-portal.legal.plan.publish'
        : val.name !== 'law-portal.legal.plan.deactivate';
    });

    return (
      <Grid>
        {actionIcon?.map((item) => (
          <Tooltip title={formatMessage({ id: item.name })} key={item.id}>
            <IconButton
              style={{ cursor: 'pointer' }}
              onClick={(event) => item.onClick && item.onClick(event, { planData })}
            >
              {item.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Grid>
    );
  }), [actionData, formatMessage]);

  const newPlanCreate = useCallback(() => {
    setNewCreate((prev) => !prev);
    setDuplicatePlanData('');
    setEditData("");
  }, []);

  // Memoize column definitions
  const columnPlan = useMemo(() => [
    { 
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.name-of-control' }), 
      field: 'name' 
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Company-unit' }),
      field: 'customerCompanyUnitId',
      render: (row: PlanData) => row?.customerCompanyUnitId?.name,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Start-date' }),
      field: 'startDate',
      render: (row: PlanData) => moment(row?.startDate).format('YYYY-MM-DD'),
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.End-date' }), 
      field: 'endDate',
      render: (row: PlanData) => moment(row?.endDate).format('YYYY-MM-DD'), 
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Recuring-interval' }),
      field: 'recurrentInterval',
      render: (row: PlanData) => row?.recurrentInterval || "-",
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Number-of-controls' }),
      field: 'lawCount',
      render: (row: PlanData) => row.lawCount,
      sorting: false,
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Edit' }),
      field: '',
      width: '20%',
      render: (row: PlanData) => <IconsNode planData={row} />,
      sorting: false,
    },
  ], [formatMessage, IconsNode]);

  const allPlanColumns = useMemo(() => [
    { 
      title: formatMessage({ id: 'law-portal.our-laws.table.number' }), 
      field: 'number',
      sorting: false
    },
    {
      title: formatMessage({ id: 'law-portal.our-laws.table.law' }),
      field: 'legislation',
      sorting: false, 
    },
    { 
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.name-of-control' }), 
      field: 'name',
      sorting: false
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Company-unit' }),
      field: 'customerCompanyUnitId',
      render: (row: PlanData) => row?.customerCompanyUnitId?.name,
      sorting: false
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Start-date' }),
      field: 'startDate',
      render: (row: PlanData) => moment(row?.startDate).format('YYYY-MM-DD'),
      sorting: false
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.End-date' }), 
      field: 'endDate',
      render: (row: PlanData) => moment(row?.endDate).format('YYYY-MM-DD'), 
      sorting: false
    },
    {
      title: formatMessage({ id: 'law-portal.compliance-law.plan-compliance.Recuring-interval' }),
      field: 'recurrentInterval',
      render: (row: PlanData) => row?.recurrentInterval || "-",
      sorting: false
    },
    {
      title: formatMessage({ id: 'law-portal.compliance.report.table.col' }),
      field: 'responsible',
      render: (row: PlanData) => {
        if (row.responsibles) {
          const responsibleArr = row.responsibles.split(',');
          return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {responsibleArr.map((val: string, idx: number) => (
                <span key={idx}>{val}</span>
              ))}
            </div>
          );
        }
        return "-";
      },
      sorting: false
    },
  ], [formatMessage]);

  const ToolbarComponent = useCallback((props: any) => (
    <div className={classes.toolbar} style={{ display: 'flex', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex' }}>
        <div
          className={`${classes.tabsize} ${activeTab === "active" ? classes?.tabActive : classes?.tab}`}
          onClick={() => setActiveTab('active')}
        >
          {formatMessage({ id: 'law-portal.legal.planned.law.comliance.check' })}
          <span className={classes?.tabNumber}>{activePlanList?.length}</span>
        </div>
        <div
          className={`${classes.tabsize} ${activeTab === "all" ? classes?.tabActive : classes?.tab}`}
          onClick={() => setActiveTab('all')}
        >
          {formatMessage({ id: 'law-portal.compliance-law.plan-compliance.all-control' })}
          <span className={classes?.tabNumber}>{allControl?.length}</span>
        </div>
        <div
          className={`${classes.tabsize} ${activeTab === "deactive" ? classes?.tabActive : classes?.tab}`}
          onClick={() => setActiveTab('deactive')}
        >
          {formatMessage({ id: 'law-portal.compliance-law.plan-compliance.deactive-control' })}
          <span className={classes?.tabNumber}>{inActivePlanList?.length}</span>
        </div>
      </div>
      <MTableToolbar {...props} />
    </div>
  ), [activeTab, classes, activePlanList, allControl, inActivePlanList, formatMessage]);

  return (
    <div className={classes.listWrapper}>
      {!newCreate && <Alert severity="warning" style={{ marginBottom: '16px' }}>
            {formatMessage({ id: 'law-portal.profile.applicationVersion.icon.text' })}
                    {' '}
                    <a
                      href="https://help.lagportalen.se/sv/collections/236844-early-access"
                      target="_blank"
                      rel="noopener noreferrer"
                       style={{ color: '#c4122f', textDecoration: 'underline', fontWeight: 600 }}
                    >
                      {formatMessage({ id: 'law-portal.profile.applicationVersion.read-more' })}
                    </a>
            </Alert>}
      {newCreate ? (
        <ButtonLink
          text={formatMessage({ id: 'component.back-button.back' })}
          onclick={newPlanCreate}
          to={''}
          icon={<ArrowBack />}
        />
      ) : (
        <ButtonLink
          text={formatMessage({ id: 'law-portal.legal.complaince.create.new.planning' })}
          onclick={newPlanCreate}
          to={''}
          className={classes.text}
        />
      )}
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />
      {newCreate ? (
        <Grid item style={{ marginTop: 20 }}>
          <CreatePlannig
            setNewCreate={setNewCreate}
            newCreate={newCreate}
            getCompliancePlan={getCompliancePlan}
            handleAllControl={handleAllControl}
            isEdit={!!editData}
            planData={duplicatePlanData || editData}
            setDuplicatePlanData={setDuplicatePlanData}
            setEditData={setEditData}
            isDuplicate={!!duplicatePlanData}
          />
        </Grid>
      ) : (
        <>
         
            <ExecuteFilter 
              filters={filters} 
              setFilter={setFilter} 
              search={search} 
              setSearch={setSearch} 
            />
            <hr style={{ marginTop: '25px', marginBottom: "25px" }} />
             <Transition isLoading={planLoading}>
            {/* Active Plans Table */}
            <div style={{ display: activeTab === 'active' ? 'block' : 'none' }}>
              <MTable
                columns={columnPlan}
                data={activePlanList}
                components={{
                  Toolbar: ToolbarComponent
                }}
                options={{
                  detailPanelColumnAlignment: 'right',
                  pageSizeOptions: [10, 25, 50, 100],
                }}
                detailPanel={[
                  {
                    tooltip: formatMessage({ id: "text.expend" }),
                    render: (rowData: any) => <PlanningView planLawsData={rowData?.rowData} />,
                  }
                ]}
              />
            </div>

            {/* All Controls Table */}
            <div style={{ display: activeTab === 'all' ? 'block' : 'none' }}>
              <MTable
                columns={allPlanColumns}
                data={allControl}
                components={{
                  Toolbar: ToolbarComponent
                }}
                options={{
                  pageSizeOptions: [10, 25, 50, 100],
                }}
              />
            </div>

            {/* Deactive Plans Table */}
            <div style={{ display: activeTab === 'deactive' ? 'block' : 'none' }}>
              <MTable
                columns={columnPlan}
                data={inActivePlanList}
                components={{
                  Toolbar: ToolbarComponent
                }}
                options={{
                  detailPanelColumnAlignment: 'right',
                  pageSizeOptions: [10, 25, 50, 100],
                }}
                detailPanel={[
                  {
                    tooltip: formatMessage({ id: "text.expend" }),
                    render: (rowData: any) => <PlanningView planLawsData={rowData?.rowData} />,
                  }
                ]}
              />
            </div>
          </Transition>
        </>
      )}
    </div>
  );
});

OldPlanningLK.displayName = 'OldPlanningLK';

export default OldPlanningLK;